import { createContext, Dispatch, PropsWithChildren, SetStateAction, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export type NavBarContextType = {
  backHandler: () => void;
  pageTitle?: string;
  setBackHandler: Dispatch<SetStateAction<() => void>>;
  setPageTitle: (x: string | undefined) => void;
};

export const NavBarContext = createContext<NavBarContextType>({} as NavBarContextType);

type UseNavBarContextParams = {
  callback?: () => void;
  title?: string;
};

export const useNavBarContext = ({ callback, title }: UseNavBarContextParams = {}) => {
  const location = useLocation();
  const { setBackHandler, backHandler, setPageTitle, pageTitle } = useContext(NavBarContext);

  const callbackFn = useCallback(() => (callback ? callback() : undefined), [callback]);

  useEffect(() => {
    if (callback) {
      setBackHandler(() => callbackFn);
    }
    if (title) {
      setPageTitle(title);
    } else {
      setPageTitle(undefined);
    }
  }, [callback, callbackFn, location?.pathname, setBackHandler, setPageTitle, title]);

  return { backHandler, pageTitle };
};

export const NavBarContextProvider = (props: PropsWithChildren) => {
  const navigate = useNavigate();
  const location = useLocation();
  const navigateBack = useCallback(() => navigate(-1), [navigate]);

  const [navigateFn, setNavigateFn] = useState<() => void>(() => navigateBack);
  const [pageTitle, setPageTitle] = useState<string | undefined>();

  const resetPathnameRef = useRef(location?.pathname);
  if (resetPathnameRef.current !== location?.pathname) {
    resetPathnameRef.current = location?.pathname;
    setNavigateFn(() => navigateBack);
  }

  const providerValue = useMemo(() => ({ backHandler: navigateFn, setBackHandler: setNavigateFn, pageTitle, setPageTitle }), [navigateFn, pageTitle]);

  return <NavBarContext.Provider value={providerValue}>{props.children}</NavBarContext.Provider>;
};
